
import { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import { Link } from 'react-router-dom';
import Header from './components/Header';
import AnnouncementsSection from './components/AnnouncementsSection';
import { PhotoInteractions } from '../../components/feature/PhotoInteractions';
import { SuggestionBox } from '../../components/feature/SuggestionBox';

// Stats Card Component
interface StatsCardProps {
  title: string;
  value: string;
  icon: string;
  color: 'blue' | 'green' | 'purple' | 'indigo';
}

function StatsCard({ title, value, icon, color }: StatsCardProps) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    indigo: 'bg-indigo-50 text-indigo-600',
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
        </div>
        <div className={`w-12 h-12 rounded-lg ${colorClasses[color]} flex items-center justify-center`}>
          <i className={`${icon} text-2xl`}></i>
        </div>
      </div>
    </div>
  );
}

export default function EmployeeDashboard() {
  const { user, userProfile } = useAuth();

  const [uploadedPhotos, setUploadedPhotos] = useState<
    Array<{
      id: string;
      url: string;
      name: string;
      uploadDate: string;
      type: string;
      uploadedBy?: string;
      storagePath?: string;
      folderId?: string;
      folderName?: string;
    }>
  >([]);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [folders, setFolders] = useState<
    Array<{
      id: string;
      folder_name: string;
      description?: string;
    }>
  >([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState<string>('');
  const [showFolderManager, setShowFolderManager] = useState(false);
  const [folderManagerMode, setFolderManagerMode] = useState<'create' | 'edit'>('create');
  const [editingFolder, setEditingFolder] = useState<{
    id: string;
    name: string;
    description?: string;
  } | null>(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderDescription, setNewFolderDescription] = useState('');
  const [selectedFilterFolder, setSelectedFilterFolder] = useState<string>('all');

  const [stats, setStats] = useState({
    totalPolicies: 0,
    totalBrokerFee: 0,
    avgBrokerFee: 0,
    agentCount: 0,
    totalRoadsides: 0,
  });

  const [pageLoading, setPageLoading] = useState(true);
  const [employeeGrade, setEmployeeGrade] = useState<{
    grade: string;
    salesAgent: string;
    policiesSold: string;
    brokerFeeAverage: string;
    brokerFeeTotal: string;
    roadsides: string;
    dailyAppAvg: string;
    tasks: string;
    chargebacks: string;
    profilePhoto?: string;
    payrollTotal?: string; // Added payroll total field
  } | null>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Daily welcome messages
  const welcomeMessages = [
    {
      message: "Every policy you sell changes a life. Today is your day to make a difference!",
      emoji: "🌟",
    },
    {
      message: "Your dedication inspires us all. Let's make today count!",
      emoji: "💪",
    },
    {
      message: "Success is built one conversation at a time. You've got this!",
      emoji: "🚀",
    },
    {
      message: "Your passion for helping families is what makes you exceptional!",
      emoji: "❤️",
    },
    {
      message: "Today is full of opportunities. Go out there and shine!",
      emoji: "✨",
    },
    {
      message: "You're not just selling insurance, you're providing peace of mind!",
      emoji: "🛡️",
    },
    {
      message: "Your hard work and commitment make all the difference. Keep pushing forward!",
      emoji: "🎯",
    },
  ];

  // Get daily message based on day of year
  const getDailyMessage = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), 0, 0);
    const diff = now.getTime() - start.getTime();
    const oneDay = 1000 * 60 * 60 * 24;
    const dayOfYear = Math.floor(diff / oneDay);
    const index = dayOfYear % welcomeMessages.length;
    return welcomeMessages[index];
  };

  const dailyMessage = getDailyMessage();

  // Get user's first name from email
  const getUserFirstName = () => {
    if (!user?.email) return "Team Member";
    const emailName = user.email.split("@")[0];
    const firstName = emailName.split(".")[0];
    return firstName.charAt(0).toUpperCase() + firstName.slice(1);
  };

  // Employee mapping for matching
  const employeeMapping = {
    'AJ Santiago': { firstName: 'AJ', email: 'AJ@driveninsurance.com' },
    'Adrian Medrano': { firstName: 'Adrian', email: 'Adrian@driveninsurance.com' },
    'Alexander Lara': { firstName: 'Alexander', email: 'Alexander@driveninsurance.com' },
    'Fernando Bochas': { firstName: 'Fernando', email: 'Fernando@driveninsurance.com' },
    'Jeffrey Wilson': { firstName: 'Jeffrey', email: 'Jeffrey@driveninsurance.com' },
    'Jenny Arvizu': { firstName: 'Jenny', email: 'Jenny@driveninsurance.com' },
    'Jesse Cheguina': { firstName: 'Jesse', email: 'Jesse@driveninsurance.com' },
    'Paul Sims': { firstName: 'Paul', email: 'Paul@driveninsurance.com' },
    'Shanitan Harrell': { firstName: 'Shanitan', email: 'Shanitan@driveninsurance.com' },
    'Steven Simpson': { firstName: 'Steven', email: 'Steven@driveninsurance.com' },
    'Yesenia Pena': { firstName: 'Yesenia', email: 'Yesenia@driveninsurance.com' },
    'Yulisa Pena': { firstName: 'Yulisa', email: 'Yulisa@driveninsurance.com' },
  };

  // Helper function to match employee data
  const matchEmployee = (salesAgent: string, userEmail: string, userFullName?: string | null): boolean => {
    if (!salesAgent) return false;

    const salesAgentLower = salesAgent.toLowerCase().trim();
    const userEmailLower = userEmail.toLowerCase().trim();

    // Try to find matching employee in mapping
    for (const [fullName, data] of Object.entries(employeeMapping)) {
      const fullNameLower = fullName.toLowerCase();
      const firstNameLower = data.firstName.toLowerCase();
      const emailLower = data.email.toLowerCase();

      // Check if current user matches this employee
      const isCurrentUser =
        userEmailLower === emailLower ||
        userFullName?.toLowerCase().trim() === fullNameLower ||
        userEmailLower.startsWith(firstNameLower + '@');

      if (!isCurrentUser) continue;

      // Now check if the sales agent matches this employee
      const matches =
        salesAgentLower === fullNameLower ||
        salesAgentLower === firstNameLower ||
        salesAgentLower.includes(firstNameLower) ||
        salesAgentLower.includes(fullNameLower);

      if (matches) {
        console.log(`🎯 MATCH FOUND: "${salesAgent}" matches employee "${fullName}"`);
        return true;
      }
    }

    return false;
  };

  // Load folders
  const loadFolders = async () => {
    try {
      const { data: folderData, error } = await supabase
        .from("media_folders")
        .select("*")
        .order("created_at", { ascending: true });

      if (error) throw error;

      setFolders(folderData || []);

      // Check admin role - fix the query
      if (user?.id) {
        const { data: profile, error: profileError } = await supabase
          .from("user_profiles")
          .select("role")
          .eq("user_id", user.id)
          .maybeSingle();

        if (profileError) {
          console.error("Error fetching user profile:", profileError);
          setIsAdmin(false);
        } else {
          setIsAdmin(profile?.role === "admin");
        }
      }

      const generalFolder = folderData?.find((f) => f.folder_name === "General");
      if (generalFolder) setSelectedFolderId(generalFolder.id);
    } catch (err) {
      console.error("Error loading folders:", err);
    }
  };

  // Load media uploads
  const loadMediaUploads = async () => {
    try {
      const { data: mediaData, error } = await supabase
        .from("media_uploads")
        .select(`
          *,
          media_folders(folder_name)
        `)
        .order("uploaded_at", { ascending: false });

      if (error) throw error;

      const formattedMedia = (mediaData ?? []).map((media: any) => ({
        id: media.id,
        url: media.public_url,
        name: media.file_name,
        uploadDate: new Date(media.uploaded_at ?? Date.now()).toLocaleDateString(),
        type: media.file_type?.startsWith("video/") ? "video" : "image",
        uploadedBy: media.uploaded_by,
        storagePath: media.storage_path,
        folderId: media.folder_id,
        folderName: media.media_folders?.folder_name ?? "No Folder",
      }));

      setUploadedPhotos(formattedMedia);
    } catch (err) {
      console.error("Error loading media uploads:", err);
    }
  };

  // Fetch employee data (modified)
  useEffect(() => {
    const fetchEmployeeData = async () => {
      if (!user) {
        setLoading(false);
        setPageLoading(false);
        return;
      }

      try {
        setLoading(true);
        setPageLoading(true);
        setError('');

        console.log('🔍 Starting employee data fetch for user:', user.email);

        // Employee mapping with all 3 identifiers
        const employeeMap: { [key: string]: { fullName: string; firstName: string; email: string; photo: string } } = {
          'aj': { fullName: 'AJ Santiago', firstName: 'AJ', email: 'AJ@driveninsurance.com', photo: 'https://static.readdy.ai/image/179d4a3273ef4ee52c79cd3980b80e29/f2329cfbcf33ae9b0f2d8a91c6da9b69.png' },
          'adrian': { fullName: 'Adrian Medrano', firstName: 'Adrian', email: 'Adrian@driveninsurance.com', photo: 'https://static.readdy.ai/image/179d4a3273ef4ee52c79cd3980b80e29/1cfddd0c323faee2e871d38fa787a874.png' },
          'alexander': { fullName: 'Alexander Lara', firstName: 'Alexander', email: 'Alexander@driveninsurance.com', photo: 'https://static.readdy.ai/image/179d4a3273ef4ee52c79cd3980b80e29/9506a91e2b948a80a95df4f717459f04.png' },
          'carlos': { fullName: 'Carlos Admin', firstName: 'Carlos', email: 'carlos@driveninsurance.com', photo: 'https://static.readdy.ai/image/179d4a3273ef4ee52c79cd3980b80e29/f2329cfbcf33ae9b0f2d8a91c6da9b69.png' },
          'fernando': { fullName: 'Fernando Bochas', firstName: 'Fernando', email: 'Fernando@driveninsurance.com', photo: 'https://static.readdy.ai/image/179d4a3273ef4ee52c79cd3980b80e29/ca3a7c70ce6454b10b56db8c687bdb75.png' },
          'jeffrey': { fullName: 'Jeffrey Wilson', firstName: 'Jeffrey', email: 'Jeffrey@driveninsurance.com', photo: 'https://static.readdy.ai/image/179d4a3273ef4ee52c79cd3980b80e29/4992c88e639f7fa3308eca513522c5b1.png' },
          'jenny': { fullName: 'Jenny Arvizu', firstName: 'Jenny', email: 'Jenny@driveninsurance.com', photo: 'https://static.readdy.ai/image/179d4a3273ef4ee52c79cd3980b80e29/38a681727cc72d08123d7abd82cb55f3.png' },
          'jesse': { fullName: 'Jesse Cheguina', firstName: 'Jesse', email: 'Jesse@driveninsurance.com', photo: 'https://static.readdy.ai/image/179d4a3273ef4ee52c79cd3980b80e29/7d761e0dd331f2381c412ba047eb3c0a.png' },
          'paul': { fullName: 'Paul Sims', firstName: 'Paul', email: 'Paul@driveninsurance.com', photo: 'https://static.readdy.ai/image/179d4a3273ef4ee52c79cd3980b80e29/09fe48f131ff9b7d3d07da969b285d32.png' },
          'shanitan': { fullName: 'Shanitan Harrell', firstName: 'Shanitan', email: 'Shanitan@driveninsurance.com', photo: 'https://static.readdy.ai/image/179d4a3273ef4ee52c79cd3980b80e29/96dcddae3106e07139a385ae3570b561.png' },
          'steven': { fullName: 'Steven Simpson', firstName: 'Steven', email: 'Steven@driveninsurance.com', photo: 'https://static.readdy.ai/image/179d4a3273ef4ee52c79cd3980b80e29/958a53a03973a40f1fd7d402c4e31057.png' },
          'yesenia': { fullName: 'Yesenia Pena', firstName: 'Yesenia', email: 'Yesenia@driveninsurance.com', photo: 'https://static.readdy.ai/image/179d4a3273ef4ee52c79cd3980b80e29/6952c9d71aed10b00d0b21770a6d2ee4.png' },
          'yulisa': { fullName: 'Yulisa Pena', firstName: 'Yulisa', email: 'Yulisa@driveninsurance.com', photo: 'https://static.readdy.ai/image/179d4a3273ef4ee52c79cd3980b80e29/2bde41cba6dac6240f8e8c37ff03ce3a.png' },
          'service64': { fullName: 'Valerina PH Retention', firstName: 'Valerina', email: 'service64@driveninsurance.com', photo: 'https://static.readdy.ai/image/179d4a3273ef4ee52c79cd3980b80e29/f2329cfbcf33ae9b0f2d8a91c6da9b69.png' },
        };

        // Get employee identifiers based on logged-in user's email
        const userEmailLower = (user.email || '').toLowerCase();
        const userKey = userEmailLower.split('@')[0];
        const employeeInfo = employeeMap[userKey];

        if (!employeeInfo) {
          console.error('❌ User email not found in employee mapping:', user.email);
          setError('Your account is not recognized. Please contact your administrator.');
          setLoading(false);
          setPageLoading(false);
          return;
        }

        console.log('✅ Employee identifiers found:', employeeInfo);

        // Fetch data from Owner View table
        const { data: ownersData, error: ownersError } = await supabase
          .from('Owner View')
          .select('*');

        console.log('📊 Raw Supabase response:', { ownersData, ownersError });

        if (ownersError) {
          console.error('❌ Error fetching Owner View data:', ownersError);
          setError('Failed to load performance data. Please try again later.');
          setEmployeeGrade({
            grade: 'N/A',
            salesAgent: employeeInfo.firstName,
            policiesSold: '0',
            brokerFeeAverage: '$0',
            brokerFeeTotal: '$0',
            roadsides: '0',
            dailyAppAvg: '0',
            tasks: '0',
            chargebacks: '0',
            profilePhoto: employeeInfo.photo,
            payrollTotal: '$0',
          });
          setLoading(false);
          setPageLoading(false);
          return;
        }

        if (!ownersData || ownersData.length === 0) {
          console.error('❌ No data returned from Owner View table');
          setError('No data available in the Owner View table. Please contact your administrator.');
          setEmployeeGrade({
            grade: 'N/A',
            salesAgent: employeeInfo.firstName,
            policiesSold: '0',
            brokerFeeAverage: '$0',
            brokerFeeTotal: '$0',
            roadsides: '0',
            dailyAppAvg: '0',
            tasks: '0',
            chargebacks: '0',
            profilePhoto: employeeInfo.photo,
            payrollTotal: '$0',
          });
          setLoading(false);
          setPageLoading(false);
          return;
        }

        console.log('📊 Owner View data fetched:', ownersData.length, 'records');

        // Find employee data by matching ANY of the three identifiers in sales_agents column
        const employeeData = ownersData.find((row) => {
          const salesAgent = (row.sales_agents || '').trim();
          const salesAgentLower = salesAgent.toLowerCase();

          const matchesFullName = salesAgentLower === employeeInfo.fullName.toLowerCase();
          const matchesFirstName = salesAgentLower === employeeInfo.firstName.toLowerCase();
          const matchesEmail = salesAgentLower === employeeInfo.email.toLowerCase();

          const isMatch = matchesFullName || matchesFirstName || matchesEmail;

          if (isMatch) {
            console.log('✅ Match found! Sales Agent:', salesAgent);
          }

          return isMatch;
        });

        if (!employeeData) {
          console.error('❌ No matching employee data found in Owner View');
          setError(`No performance data found for ${employeeInfo.firstName}. Please contact your administrator.`);
          setEmployeeGrade({
            grade: 'N/A',
            salesAgent: employeeInfo.firstName,
            policiesSold: '0',
            brokerFeeAverage: '$0',
            brokerFeeTotal: '$0',
            roadsides: '0',
            dailyAppAvg: '0',
            tasks: '0',
            chargebacks: '0',
            profilePhoto: employeeInfo.photo,
            payrollTotal: '$0',
          });
          setLoading(false);
          setPageLoading(false);
          return;
        }

        console.log('✅ Employee data found:', employeeData);

        // Fetch roadsides from mtd_sales_data table
        const { data: mtdData, error: mtdError } = await supabase
          .from('mtd_sales_data')
          .select('roadsides, sales_agent');

        console.log('📊 MTD Sales data response:', { mtdData, mtdError });

        let roadsides = '0';
        if (!mtdError && mtdData && mtdData.length > 0) {
          // Find matching employee in MTD data
          const mtdEmployee = mtdData.find((row) => {
            const salesAgent = (row.sales_agent || '').trim();
            const salesAgentLower = salesAgent.toLowerCase();

            const matchesFullName = salesAgentLower === employeeInfo.fullName.toLowerCase();
            const matchesFirstName = salesAgentLower === employeeInfo.firstName.toLowerCase();
            const matchesEmail = salesAgentLower === employeeInfo.email.toLowerCase();

            return matchesFullName || matchesFirstName || matchesEmail;
          });

          if (mtdEmployee) {
            console.log('✅ MTD employee data found:', mtdEmployee);
            const roadsidesValue = mtdEmployee.roadsides;
            if (typeof roadsidesValue === 'number') {
              roadsides = roadsidesValue.toString();
            } else if (typeof roadsidesValue === 'string') {
              roadsides = roadsidesValue;
            }
            console.log('📊 Roadsides value:', roadsides);
          } else {
            console.log('⚠️ No matching employee found in MTD data');
          }
        } else {
          console.log('⚰️ Could not fetch MTD data for roadsides');
        }

        // Fetch payroll total from US Payroll table
        const { data: payrollData, error: payrollError } = await supabase
          .from('US Payroll')
          .select('total_pay, agent');

        console.log('📊 US Payroll data response:', { payrollData, payrollError });

        let payrollTotal = '$0';
        if (!payrollError && payrollData && payrollData.length > 0) {
          // Find matching employee in payroll data
          const payrollEmployee = payrollData.find((row) => {
            const agentName = (row.agent || '').trim();
            const agentNameLower = agentName.toLowerCase();

            const matchesFullName = agentNameLower === employeeInfo.fullName.toLowerCase();
            const matchesFirstName = agentNameLower === employeeInfo.firstName.toLowerCase();
            const matchesEmail = agentNameLower === employeeInfo.email.toLowerCase();

            return matchesFullName || matchesFirstName || matchesEmail;
          });

          if (payrollEmployee) {
            console.log('✅ Payroll employee data found:', payrollEmployee);
            const totalPayValue = payrollEmployee.total_pay;
            if (totalPayValue) {
              // If it's already formatted with $, use it as is
              if (typeof totalPayValue === 'string' && totalPayValue.includes('$')) {
                payrollTotal = totalPayValue;
              } else {
                // Otherwise format it
                const numValue = typeof totalPayValue === 'number' 
                  ? totalPayValue 
                  : parseFloat(totalPayValue.toString().replace(/[$,]/g, ''));
                payrollTotal = `$${numValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
              }
            }
            console.log('📊 Payroll total value:', payrollTotal);
          } else {
            console.log('⚠️ No matching employee found in US Payroll data');
          }
        } else {
          console.log('⚠️ Could not fetch US Payroll data');
        }

        // Set employee grade data with correct column names and profile photo
        setEmployeeGrade({
          grade: employeeData.grades || 'N/A',
          salesAgent: employeeData.sales_agents || employeeInfo.firstName,
          policiesSold: employeeData.pol_sold || '0',
          brokerFeeAverage: employeeData.bf_avg || '$0',
          brokerFeeTotal: employeeData.total_broker_fee || '$0',
          roadsides: roadsides,
          dailyAppAvg: employeeData.daily_app_avg || '0',
          tasks: employeeData.tasks || '0',
          chargebacks: employeeData.chargebacks || '0',
          profilePhoto: employeeInfo.photo,
          payrollTotal: payrollTotal,
        });

        console.log('✅ Employee grade set successfully');

        // NEW: Fetch company stats from mtd_sales_data
        const { data: mtdStatsData, error: mtdStatsError } = await supabase
          .from('mtd_sales_data')
          .select('*');

        if (!mtdStatsError && mtdStatsData && mtdStatsData.length > 0) {
          // Find the Total row
          const totalRow = mtdStatsData.find(row => row.sales_agent === 'Total');
          const employees = mtdStatsData.filter(row => row.sales_agent !== 'Total');

          if (totalRow) {
            const totalPolicies = totalRow.policies_sold 
              ? parseInt(totalRow.policies_sold.replace(/,/g, '')) 
              : 0;
            const avgBrokerFee = totalRow.broker_fee_average 
              ? parseFloat(totalRow.broker_fee_average.replace(/[$,]/g, '')) 
              : 0;
            
            // Calculate total roadsides from all employees
            const totalRoadsides = employees.reduce((sum, emp) => {
              const roadsidesValue = emp.roadsides;
              let roadsides = 0;
              if (typeof roadsidesValue === 'number') {
                roadsides = roadsidesValue;
              } else if (typeof roadsidesValue === 'string') {
                roadsides = parseInt(roadsidesValue.replace(/,/g, '')) || 0;
              }
              return sum + roadsides;
            }, 0);

            setStats({
              totalPolicies: totalPolicies,
              totalBrokerFee: 0, // Not used in employee dashboard
              avgBrokerFee: avgBrokerFee,
              agentCount: employees.length,
              totalRoadsides: totalRoadsides,
            });

            console.log('✅ Company stats loaded from mtd_sales_data:', {
              totalPolicies,
              avgBrokerFee,
              agentCount: employees.length,
              totalRoadsides
            });
          }
        }

      } catch (err) {
        console.error('❌ Error in fetchEmployeeData:', err);
        setError('An unexpected error occurred. Please try again later.');
      } finally {
        setLoading(false);
        setPageLoading(false);
      }
    };

    fetchEmployeeData();
  }, [user]);

  // Load folders and media on mount
  useEffect(() => {
    loadFolders();
    loadMediaUploads();
  }, []);

  // Photo upload handler
  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    setMediaLoading(true);
    const uploadPromises = Array.from(files).map(async (file) => {
      if (file.type.startsWith("image/") || file.type.startsWith("video/")) {
        try {
          const fileExt = file.name.split(".").pop();
          const fileName = `${Date.now()}_${Math.random()
            .toString(36)
            .substring(2)}.${fileExt}`;
          const filePath = `uploads/${fileName}`;

          // Upload to storage
          const { error: uploadError } = await supabase.storage
            .from("media")
            .upload(filePath, file);

          if (uploadError) throw uploadError;

          // Get public URL
          const { data: publicData } = supabase.storage
            .from("media")
            .getPublicUrl(filePath);

          const publicUrl = publicData?.publicUrl;

          // Insert record
          const { error: dbError } = await supabase
            .from("media_uploads")
            .insert({
              file_name: file.name,
              file_type: file.type,
              file_size: file.size,
              storage_path: filePath,
              public_url: publicUrl,
              uploaded_by: user?.email || "Unknown",
              folder_id: selectedFolderId || null,
            });

          if (dbError) throw dbError;

          return true;
        } catch (err) {
          console.error(`Error uploading ${file.name}:`, err);
          return false;
        }
      }
      return false;
    });

    try {
      const results = await Promise.all(uploadPromises);
      const successCount = results.filter(Boolean).length;

      if (successCount > 0) {
        await loadMediaUploads();

        if (successCount === files.length) {
          alert(`Successfully uploaded ${successCount} file${successCount > 1 ? "s" : ""}!`);
        } else {
          alert(`Uploaded ${successCount} out of ${files.length} files. Some uploads failed.`);
        }
      } else {
        alert("Failed to upload files. Please try again.");
      }
    } catch (err) {
      console.error("Error during upload process:", err);
      alert("An error occurred during upload. Please try again.");
    } finally {
      setMediaLoading(false);
      event.target.value = "";
    }
  };

  // Delete photo handler
  const handleDeletePhoto = async (photoId: string, storagePath?: string) => {
    if (!storagePath) {
      console.error("No storage path provided for deletion");
      alert("Cannot delete: Missing storage path information");
      return;
    }

    const confirmDelete = window.confirm(
      "Are you sure you want to permanently delete this media? This action cannot be undone and will remove all comments and reactions."
    );
    if (!confirmDelete) return;

    try {
      setUploadedPhotos((prev) => prev.filter((photo) => photo.id !== photoId));

      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from("media")
        .remove([storagePath]);
      if (storageError) throw storageError;

      // Delete related data
      await supabase.from("photo_reactions").delete().eq("media_id", photoId);
      await supabase.from("photo_comments").delete().eq("media_id", photoId);
      await supabase.from("comment_responses").delete().eq("media_id", photoId);

      // Delete DB record
      const { error: dbError } = await supabase
        .from("media_uploads")
        .delete()
        .eq("id", photoId);
      if (dbError) throw dbError;

      await loadMediaUploads();
      alert("Media and all associated data deleted successfully!");
    } catch (err) {
      console.error("Error during deletion process:", err);
      await loadMediaUploads();
      alert("An error occurred while deleting. Please try again.");
    }
  };

  // Folder management handlers
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      alert("Please enter a folder name");
      return;
    }

    try {
      const { error } = await supabase
        .from("media_folders")
        .insert({
          folder_name: newFolderName.trim(),
          description: newFolderDescription.trim() || null,
          created_by: user?.id || null,
        });

      if (error) throw error;

      await loadFolders();
      setNewFolderName("");
      setNewFolderDescription("");
      setShowFolderManager(false);
      alert("Folder created successfully!");
    } catch (err) {
      console.error("Error creating folder:", err);
      alert("Failed to create folder. Please try again.");
    }
  };

  const handleUpdateFolder = async () => {
    if (!editingFolder || !newFolderName.trim()) {
      alert("Please enter a folder name");
      return;
    }

    try {
      const { error } = await supabase
        .from("media_folders")
        .update({
          folder_name: newFolderName.trim(),
          description: newFolderDescription.trim() || null,
        })
        .eq("id", editingFolder.id);

      if (error) throw error;

      await loadFolders();
      setNewFolderName("");
      setNewFolderDescription("");
      setEditingFolder(null);
      setShowFolderManager(false);
      alert("Folder updated successfully!");
    } catch (err) {
      console.error("Error updating folder:", err);
      alert("Failed to update folder. Please try again.");
    }
  };

  const handleDeleteFolder = async (folderId: string, folderName: string) => {
    if (
      !window.confirm(
        `Are you sure you want to delete the "${folderName}" folder? All media in this folder will be moved to the General folder.`
      )
    ) {
      return;
    }

    try {
      const generalFolder = folders.find((f) => f.folder_name === "General");

      if (generalFolder) {
        await supabase
          .from("media_uploads")
          .update({ folder_id: generalFolder.id })
          .eq("folder_id", folderId);
      }

      const { error } = await supabase
        .from("media_folders")
        .delete()
        .eq("id", folderId);
      if (error) throw error;

      await Promise.all([loadFolders(), loadMediaUploads()]);

      if (selectedFolderId === folderId) {
        setSelectedFolderId(generalFolder?.id || "");
      }

      alert("Folder deleted successfully!");
    } catch (err) {
      console.error("Error deleting folder:", err);
      alert("Failed to delete folder. Please try again.");
    }
  };

  const openFolderManager = (
    mode: "create" | "edit",
    folder?: { id: string; folder_name: string; description?: string }
  ) => {
    setFolderManagerMode(mode);
    if (mode === "edit" && folder) {
      setEditingFolder({ id: folder.id, name: folder.folder_name, description: folder.description });
      setNewFolderName(folder.folder_name);
      setNewFolderDescription(folder.description || "");
    } else {
      setEditingFolder(null);
      setNewFolderName("");
      setNewFolderDescription("");
    }
    setShowFolderManager(true);
  };

  const filteredPhotos =
    selectedFilterFolder === "all"
      ? uploadedPhotos
      : uploadedPhotos.filter((photo) => photo.folderId === selectedFilterFolder);

  const handleRefresh = () => {
    window.location.reload();
  };

  const getGradeColor = (grade: string): string => {
    if (!grade || grade === "N/A") return "bg-gray-100 text-gray-800";
    const gradeUpper = grade.toUpperCase().trim();
    const baseGrade = gradeUpper.charAt(0);
    switch (baseGrade) {
      case "A":
        return "bg-green-100 text-green-800 border-green-300";
      case "B":
        return "bg-blue-100 text-blue-800 border-blue-300";
      case "C":
        return "bg-orange-100 text-orange-800 border-orange-300";
      case "D":
      case "F":
        return "bg-red-100 text-red-800 border-red-300";
      default:
        return "bg-gray-100 text-gray-800 border-gray-300";
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#c7f5d9' }}>
      <Header onRefresh={handleRefresh} isConnected={!error} />

      <main className="w-full px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16 py-8">
        {error && (
          <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4 max-w-4xl mx-auto">
            <div className="flex items-center">
              <i className="ri-warning-line text-yellow-600 mr-2 text-xl"></i>
              <p className="text-yellow-800 text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="mb-8">
          <div className="bg-white rounded-2xl shadow-sm p-5 max-w-4xl mx-auto">
            <div className="grid grid-cols-2 gap-3">
              <Link
                to="/philippines-sales"
                className="bg-purple-600 text-white rounded-xl p-4 text-sm font-medium transition-all shadow-sm hover:bg-purple-700 flex flex-col items-center justify-center"
              >
                <i className="ri-bar-chart-line text-xl mb-2"></i>
                <div>PH Sales MTD</div>
              </Link>

              <Link
                to="/philippines-sales-today"
                className="bg-purple-600 text-white rounded-xl p-4 text-sm font-medium transition-all shadow-sm hover:bg-purple-700 flex flex-col items-center justify-center"
              >
                <i className="ri-calendar-line text-xl mb-2"></i>
                <div>PH Sales Today</div>
              </Link>

              <Link
                to="/mtd-sales"
                className="bg-gradient-to-br from-orange-500 to-orange-600 text-white rounded-xl p-4 text-sm font-medium transition-all shadow-sm hover:from-orange-600 hover:to-orange-700 flex flex-col items-center justify-center"
              >
                <i className="ri-line-chart-line text-xl mb-2"></i>
                <div>MTD Sales</div>
              </Link>

              <Link
                to="/todays-sales"
                className="bg-gradient-to-br from-orange-500 to-orange-600 text-white rounded-xl p-4 text-sm font-medium transition-all shadow-sm hover:shadow-md hover:from-orange-600 hover:to-orange-700 flex flex-col items-center justify-center"
              >
                <i className="ri-calendar-check-line text-xl mb-2"></i>
                <div>Today's Sales</div>
              </Link>

              <Link
                to="/retention-mtd"
                className="bg-sky-600 text-white rounded-xl p-4 text-sm font-medium transition-all shadow-sm hover:bg-sky-700 flex flex-col items-center justify-center"
              >
                <i className="ri-user-heart-line text-xl mb-2"></i>
                <div>Retention MTD</div>
              </Link>

              <Link
                to="/retention-today"
                className="bg-sky-600 text-white rounded-xl p-4 text-sm font-medium transition-all shadow-sm hover:bg-sky-700 flex flex-col items-center justify-center"
              >
                <i className="ri-user-star-line text-xl mb-2"></i>
                <div>Retention Today</div>
              </Link>

              <Link
                to="/us-payroll"
                className="bg-gradient-to-br from-green-600 to-green-700 text-white rounded-xl p-4 text-sm font-medium hover:from-green-700 hover:to-green-800 transition-all shadow-sm col-span-2 flex flex-col items-center justify-center"
              >
                <i className="ri-money-dollar-circle-line text-xl mb-2"></i>
                <div>US Payroll</div>
              </Link>

              <Link
                to="/referrals"
                className="bg-gradient-to-br from-teal-500 to-teal-600 text-white rounded-xl p-4 text-sm font-medium transition-all shadow-sm hover:shadow-md hover:from-teal-600 hover:to-teal-700 flex flex-col items-center justify-center"
              >
                <i className="ri-user-add-line text-xl mb-2"></i>
                <div>My Referrals</div>
              </Link>

              <Link
                to="/tv-view"
                className="bg-gradient-to-br from-gray-700 to-gray-800 text-white rounded-xl p-4 text-sm font-medium transition-all shadow-sm hover:shadow-md hover:from-gray-800 hover:to-gray-900 flex flex-col items-center justify-center"
              >
                <i className="ri-tv-line text-xl mb-2"></i>
                <div>TV View</div>
              </Link>
            </div>
          </div>
        </div>

        {/* Welcome Message */}
        <div className="mb-8 max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <div className="bg-gradient-to-r from-green-600 to-emerald-700 px-6 py-5">
              <h1 className="text-2xl font-bold text-white">
                Welcome back, {getUserFirstName()}! {dailyMessage.emoji}
              </h1>
            </div>
            <div className="px-6 py-4">
              <p className="text-gray-700 text-lg">{dailyMessage.message}</p>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="mb-8 max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <StatsCard
              title="Total Policies Sold"
              value={stats.totalPolicies.toLocaleString()}
              icon="ri-file-text-line"
              color="blue"
            />
            <StatsCard
              title="Avg. Broker Fee"
              value={`$${Math.round(stats.avgBrokerFee).toLocaleString()}`}
              icon="ri-money-dollar-circle-line"
              color="green"
            />
            <StatsCard
              title="Active Agents"
              value={stats.agentCount.toString()}
              icon="ri-user-line"
              color="purple"
            />
            <StatsCard
              title="Roadsides"
              value={stats.totalRoadsides.toLocaleString()}
              icon="ri-roadster-line"
              color="indigo"
            />
          </div>
        </div>

        {/* Employee Performance */}
        {employeeGrade && (
          <div className="mb-8 max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-6">Your Performance</h2>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Grade Display with Profile Photo Background */}
                <div
                  className={`rounded-xl shadow-md border-2 p-8 flex flex-col items-center justify-center relative overflow-hidden ${getGradeColor(
                    employeeGrade.grade
                  )}`}
                >
                  {/* Profile Photo Background */}
                  {employeeGrade.profilePhoto && (
                    <div 
                      className="absolute inset-0 bg-cover bg-center opacity-40"
                      style={{ 
                        backgroundImage: `url(${employeeGrade.profilePhoto})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'top'
                      }}
                    ></div>
                  )}
                  
                  {/* Content */}
                  <div className="relative z-10 flex flex-col items-center">
                    <p className="text-sm font-medium opacity-80 mb-2">Current Grade</p>
                    <p className="text-6xl font-bold mb-2">{employeeGrade.grade}</p>
                    <p className="text-sm font-medium">{employeeGrade.salesAgent}</p>
                  </div>
                </div>

                {/* Performance Metrics */}
                <div className="lg:col-span-2">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div className="bg-gray-50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-gray-900">{employeeGrade.policiesSold}</p>
                      <p className="text-sm text-gray-600 mt-1">Policies Sold</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-gray-900">{employeeGrade.brokerFeeAverage}</p>
                      <p className="text-sm text-gray-600 mt-1">Avg. Broker Fee</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-gray-900">{employeeGrade.roadsides}</p>
                      <p className="text-sm text-gray-600 mt-1">Roadsides</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-gray-900">{employeeGrade.dailyAppAvg}</p>
                      <p className="text-sm text-gray-600 mt-1">Daily Apps</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-gray-900">{employeeGrade.tasks}</p>
                      <p className="text-sm text-gray-600 mt-1">Tasks</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-gray-900">{employeeGrade.chargebacks}</p>
                      <p className="text-sm text-gray-600 mt-1">Chargebacks</p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4 text-center col-span-2">
                      <p className="text-2xl font-bold text-green-900">{employeeGrade.payrollTotal}</p>
                      <p className="text-sm text-green-700 mt-1 font-medium">Payroll Total</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Announcement & Suggestions Sections */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-4xl mx-auto mb-8">
          <AnnouncementsSection />
          <SuggestionBox />
        </div>

        {/* Media Upload Section */}
        <div className="mb-8 max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Team Media</h2>
            
            {/* Upload Controls */}
            <div className="flex flex-wrap gap-4 mb-6">
              <label className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg cursor-pointer transition-colors">
                {mediaLoading ? "Uploading..." : "Upload Photo/Video"}
                <input
                  type="file"
                  accept="image/*,video/*"
                  multiple
                  onChange={handlePhotoUpload}
                  className="hidden"
                  disabled={mediaLoading}
                />
              </label>
              
              {/* Folder Manager Button */}
              {isAdmin && (
                <button
                  onClick={() => openFolderManager('create')}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
                >
                  Create Folder
                </button>
              )}
            </div>

            {/* Folder Filter Dropdown */}
            <div className="mb-6">
              <select
                value={selectedFilterFolder}
                onChange={(e) => setSelectedFilterFolder(e.target.value)}
                className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
              >
                <option value="all">All Folders</option>
                {folders.map((folder) => (
                  <option key={folder.id} value={folder.id}>
                    {folder.folder_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Media Grid */}
            <div className="space-y-6">
              {filteredPhotos.map((photo) => (
                <div key={photo.id} className="border border-gray-200 rounded-lg overflow-hidden max-w-3xl mx-auto">
                  <div className="relative group">
                    {photo.type === "video" ? (
                      <div className="relative">
                        <video 
                          src={photo.url} 
                          className="w-full h-auto object-contain"
                          controls
                        />
                      </div>
                    ) : (
                      <img
                        src={photo.url}
                        alt={photo.name}
                        className="w-full h-auto object-contain"
                      />
                    )}
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => handleDeletePhoto(photo.id, photo.storagePath)}
                        className="bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition-colors"
                      >
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    </div>
                  </div>
                  <div className="p-4 bg-gray-50 border-t border-gray-200">
                    <div className="text-sm font-medium text-gray-900 truncate">{photo.name}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      <span className="font-medium">{photo.folderName}</span>
                      <span className="mx-2">•</span>
                      Uploaded by <span className="font-medium">{photo.uploadedBy}</span> on {photo.uploadedDate}
                    </div>
                  </div>
                  <PhotoInteractions 
                    mediaId={photo.id}
                  />
                </div>
              ))}
            </div>

          </div>
        </div>

        {/* Folder Manager Modal */}
        {showFolderManager && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
              <h3 className="text-xl font-bold mb-4">
                {folderManagerMode === 'create' ? 'Create Folder' : 'Edit Folder'}
              </h3>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Folder Name
                </label>
                <input
                  type="text"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="Enter folder name"
                />
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description (Optional)
                </label>
                <textarea
                  value={newFolderDescription}
                  onChange={(e) => setNewFolderDescription(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="Enter folder description"
                  rows={3}
                />
              </div>
              
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowFolderManager(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={folderManagerMode === 'create' ? handleCreateFolder : handleUpdateFolder}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  {folderManagerMode === 'create' ? 'Create' : 'Update'}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-4 mt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-gray-500">
            © 2013 Driven Insurance Services. All rights reserved. V2.0
          </p>
        </div>
      </footer>
    </div>
  );
}
